import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchCommerces,
  getAllCommerces,
  getCommerceById,
  createCommerce,
  updateCommerce,
  deleteCommerce,
  toggleCommerceActive,
} from "../../../../services/commercesServices";

import { COMMERCES_INITIAL_FORM } from "../data/commercesAdminData";

import {
  buildCommercesSearchParams,
  formatGpsCoordinates,
  getCommercesStats,
  normalizeCommerceForm,
  validateCommerceForm,
} from "../utils/commercesAdminUtils";

export default function useCommercesAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [hasLogo, setHasLogo] = useState("all");
  const [withGps, setWithGps] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState({ ...COMMERCES_INITIAL_FORM });
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaTargetField, setMediaTargetField] = useState("logo_url");

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit), 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getCommercesStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildCommercesSearchParams({
        q,
        categorie,
        stateFilter,
        hasLogo,
        withGps,
        page,
        limit,
      });

      const res = await searchCommerces(params);

      const hasNoAdminResult =
        !res?.items?.length &&
        !q &&
        !categorie &&
        stateFilter === "all" &&
        hasLogo === "all" &&
        withGps === "all" &&
        page === 1;

      if (hasNoAdminResult) {
        const all = await getAllCommerces();
        const fallbackItems = Array.isArray(all) ? all : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        return;
      }

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement commerces :", fetchError);

      try {
        const all = await getAllCommerces();
        const fallbackItems = Array.isArray(all) ? all : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        setError(
          "Le chargement administrateur a échoué. Affichage d’une liste de secours.",
        );
      } catch (fallbackError) {
        console.error("Erreur fallback commerces :", fallbackError);

        if (!mountedRef.current) return;

        setItems([]);
        setTotal(0);
        setError("Impossible de charger les commerces pour le moment.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [q, categorie, stateFilter, hasLogo, withGps, page, limit]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeCategorie = useCallback((value) => {
    setCategorie(value);
    setPage(1);
  }, []);

  const changeStateFilter = useCallback((value) => {
    setStateFilter(value);
    setPage(1);
  }, []);

  const changeHasLogo = useCallback((value) => {
    setHasLogo(value);
    setPage(1);
  }, []);

  const changeWithGps = useCallback((value) => {
    setWithGps(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setCategorie("");
    setStateFilter("all");
    setHasLogo("all");
    setWithGps("all");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm({ ...COMMERCES_INITIAL_FORM });
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getCommerceById(id);

      if (!row) {
        setError("Commerce introuvable.");
        return;
      }

      const gpsLat =
        row.gps_lat !== null && row.gps_lat !== undefined
          ? String(row.gps_lat)
          : "";

      const gpsLng =
        row.gps_lng !== null && row.gps_lng !== undefined
          ? String(row.gps_lng)
          : "";

      setCurrentId(id);
      setForm({
        nom_commerce: row.nom_commerce || "",
        categorie: row.categorie || "",
        siret: row.siret || "",
        description_commerce: row.description_commerce || "",
        tags: row.tags || "",
        contact_nom: row.contact_nom || "",
        contact_fonction: row.contact_fonction || "",
        tel: row.tel || "",
        mail: row.mail || "",
        website: row.website || "",
        facebook: row.facebook || "",
        instagram: row.instagram || "",
        logo_url: row.logo_url || "",
        photo_url: row.photo_url || "",
        adresse: row.adresse || "",
        code_postal: row.code_postal || "",
        ville: row.ville || "",
        gps_coordinates: formatGpsCoordinates(gpsLat, gpsLng),
        gps_lat: gpsLat,
        gps_lng: gpsLng,
        moyens_paiement: row.moyens_paiement || "",
        accessibilite: row.accessibilite || "",
        services: row.services || "",
        is_active: !!row.is_active,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture commerce :", editError);
      setError("Impossible d’ouvrir ce commerce.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm({ ...COMMERCES_INITIAL_FORM });
    setFieldErrors({});
    setMediaPickerOpen(false);
    setMediaTargetField("logo_url");
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateCommerceForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError("");

      const payload = normalizeCommerceForm(form);

      if (currentId) {
        await updateCommerce(currentId, payload);
        showNotice("Commerce modifié avec succès.");
      } else {
        await createCommerce(payload);
        showNotice("Commerce créé avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement commerce :", saveError);
      setError("Impossible d’enregistrer ce commerce.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggle = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`toggle-${id}`);
        setError("");

        await toggleCommerceActive(id);
        showNotice("Statut de publication mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut commerce :", toggleError);
        setError("Impossible de modifier le statut de ce commerce.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);

        const row = await getCommerceById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu commerce :", previewError);
        setError("Impossible d’afficher l’aperçu de ce commerce.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          nom_commerce: "Ce commerce",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteCommerce(deleteItem.id);

      setDeleteItem(null);
      showNotice("Commerce supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression commerce :", deleteError);
      setError("Impossible de supprimer ce commerce.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openMediaPicker = useCallback((targetField = "logo_url") => {
    setMediaTargetField(targetField);
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleSelectMedia = useCallback(
    (media) => {
      if (!media) return;

      updateFormField(mediaTargetField, media.url || "");
      setMediaPickerOpen(false);
    },
    [mediaTargetField, updateFormField],
  );

  return {
    q,
    setQ,
    categorie,
    stateFilter,
    hasLogo,
    withGps,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,
    mediaPickerOpen,
    mediaTargetField,

    fetchData,
    onSubmitSearch,
    changeCategorie,
    changeStateFilter,
    changeHasLogo,
    changeWithGps,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggle,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,

    openMediaPicker,
    closeMediaPicker,
    handleSelectMedia,
  };
}
